import React, { useState, useId } from 'react';
import axios from 'axios';
import { useDarkMode } from './DarkModeContext';

const API = '/api';
const NAVY = '#1a2744';
const GOLD = '#c9a84c';
const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_EXT = ['doc', 'docx', 'pdf', 'txt'];
const LOCATION_OPTIONS = ['UK', 'South Africa', 'Mauritius', "Côte d'Ivoire", 'Rwanda', 'Cameroon', 'Other'];

const INTRO =
  'Enko Capital Management LLP is committed to maintaining an open and transparent working ' +
  'environment. This form allows you to raise a grievance or report a concern in confidence. ' +
  'All submissions are treated with strict confidentiality. If you choose to remain anonymous, ' +
  'we will not be able to follow up with you directly, but your concern will be reviewed and ' +
  'addressed accordingly.';

const EMPTY = {
  is_anonymous: 'no',
  concerning: '',
  investment_project: '',
  location: '',
  who_affected: '',
  description: '',
  actions_taken: '',
  resolution_requested: '',
};

export default function GrievanceForm() {
  const { isDarkMode } = useDarkMode();
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [fileErr, setFileErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const cardBg = isDarkMode ? '#1a2744' : '#ffffff';
  const textCol = isDarkMode ? '#e8e4d8' : '#1a1a2e';
  const mutedCol = isDarkMode ? '#9ba8c0' : '#666';
  const border = isDarkMode ? '1px solid rgba(201,168,76,0.25)' : '1px solid #e2e6ec';
  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '6px',
    border: isDarkMode ? '1px solid rgba(201,168,76,0.3)' : '1px solid #cdd4de',
    background: isDarkMode ? '#243058' : '#fff', color: textCol,
    fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, color: textCol, margin: '0 0 6px' };
  const groupStyle = { marginBottom: '18px' };

  const onFile = (e) => {
    const f = (e.target.files && e.target.files[0]) || null;
    setFileErr('');
    if (f) {
      const ext = f.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) { setFileErr('Accepted file types: doc, docx, pdf, txt'); setFile(null); return; }
      if (f.size > MAX_BYTES) { setFileErr('File too large. Maximum size is 5MB.'); setFile(null); return; }
    }
    setFile(f);
  };

  const submit = async () => {
    setError('');
    const required = ['concerning', 'investment_project', 'location', 'who_affected', 'description', 'actions_taken', 'resolution_requested'];
    for (const k of required) {
      if (!form[k] || !form[k].trim()) { setError('Please complete all required fields.'); return; }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('is_anonymous', form.is_anonymous === 'yes' ? 'true' : 'false');
      fd.append('concerning', form.concerning);
      fd.append('investment_project', form.investment_project);
      fd.append('location', form.location);
      fd.append('who_affected', form.who_affected);
      fd.append('description', form.description);
      fd.append('actions_taken', form.actions_taken);
      fd.append('resolution_requested', form.resolution_requested);
      if (file) fd.append('attachment', file);
      await axios.post(`${API}/grievance/submit`, fd);
      setForm(EMPTY);
      setFile(null);
      setDone(true);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.detail) || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div style={{ background: cardBg, border, borderRadius: '10px', padding: '32px', maxWidth: '760px', margin: '0 auto' }}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: GOLD, marginBottom: '10px' }}>Thank you</div>
        <p style={{ fontSize: '15px', color: textCol, margin: '0 0 20px' }}>
          Your grievance has been submitted. We will acknowledge receipt shortly.
        </p>
        <button
          onClick={() => setDone(false)}
          style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 18px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
        >
          Submit another grievance
        </button>
      </div>
    );
  }

  const textField = (key, label, { textarea, placeholder } = {}) => (
    <div style={groupStyle}>
      <label style={labelStyle}>{label} <span style={{ color: '#A63A3A' }}>*</span></label>
      {textarea ? (
        <textarea style={{ ...inputStyle, minHeight: '90px', resize: 'vertical' }} value={form[key]}
          placeholder={placeholder || ''} onChange={e => set(key, e.target.value)} />
      ) : (
        <input style={inputStyle} value={form[key]} placeholder={placeholder || ''} onChange={e => set(key, e.target.value)} />
      )}
    </div>
  );

  return (
    <div style={{ background: cardBg, border, borderRadius: '10px', padding: '28px', maxWidth: '760px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 700, color: textCol, margin: '0 0 6px' }}>Grievance / Whistleblowing</h2>
      <div style={{ height: '3px', width: '48px', background: GOLD, borderRadius: '2px', margin: '0 0 16px' }} />
      <p style={{ fontSize: '13.5px', lineHeight: 1.7, color: mutedCol, margin: '0 0 24px' }}>{INTRO}</p>

      {/* Anonymous */}
      <div style={groupStyle}>
        <label style={labelStyle}>
          Would you like to make your complaint anonymous? If you choose to be anonymous we will not be able to contact you.
        </label>
        <div style={{ display: 'flex', gap: '20px', marginTop: '6px' }}>
          {[['yes', 'Yes'], ['no', 'No']].map(([val, lab]) => (
            <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: textCol, cursor: 'pointer' }}>
              <input type="radio" name="is_anonymous" checked={form.is_anonymous === val} onChange={() => set('is_anonymous', val)} />
              {lab}
            </label>
          ))}
        </div>
      </div>

      {textField('concerning', 'What is the Grievance concerning?')}
      {textField('investment_project', 'Is it related to any investment project(s)? If so, which one(s)?', { placeholder: 'Enter project(s) or N/A' })}

      <div style={groupStyle}>
        <label style={labelStyle} htmlFor={`${a11yId}-location`}>Location <span style={{ color: '#A63A3A' }}>*</span></label>
        
        <select id={`${a11yId}-location`} style={inputStyle} value={form.location} onChange={e => set('location', e.target.value)}>
          <option value="" disabled>Select a location</option>
          {LOCATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>

      {textField('who_affected', 'Who is affected?')}
      {textField('description', 'Please describe the Grievance in detail', { textarea: true })}
      {textField('actions_taken', 'What actions have already taken place?', { textarea: true })}
      {textField('resolution_requested', 'How would you like to see the Grievance resolved?', { textarea: true })}

      {/* Attachment */}
      <div style={groupStyle}>
        <label style={labelStyle} htmlFor={`${a11yId}-please-upload-relevant-attachments-o`}>Please upload relevant attachments <span style={{ color: mutedCol, fontWeight: 400 }}>(optional: doc, docx, pdf, txt, max 5MB)</span></label>
        
        <input id={`${a11yId}-please-upload-relevant-attachments-o`} type="file" accept=".doc,.docx,.pdf,.txt" onChange={onFile} style={{ fontSize: '13px', color: textCol }} />
        {file && <div style={{ fontSize: '12px', color: mutedCol, marginTop: '4px' }}>{file.name}</div>}
        {fileErr && <div style={{ fontSize: '12px', color: '#A63A3A', marginTop: '4px' }}>{fileErr}</div>}
      </div>

      {error && <div style={{ fontSize: '13px', color: '#A63A3A', marginBottom: '14px' }}>{error}</div>}

      <button
        onClick={submit}
        disabled={submitting}
        style={{
          background: submitting ? '#8a94a8' : NAVY, color: '#fff', border: `1px solid ${GOLD}`,
          borderRadius: '6px', padding: '12px 24px', fontSize: '14px', fontWeight: 700,
          cursor: submitting ? 'default' : 'pointer',
        }}
      >
        {submitting ? 'Submitting…' : 'Submit Grievance'}
      </button>
    </div>
  );
}
